import React from 'react';
import { useNavigate } from 'react-router-dom';

import { courses } from 'services/api';
import { buildFlowPath } from 'utils/flowNavigation';
import { useDebug } from 'contexts/DebugContext';
import { RoleplayScreens } from './RoleplayScreens';
import { useRoleplaySession } from './useRoleplaySession';

/**
 * Full-page roleplay runtime for a flow whose only item is a roleplay question.
 * Roleplay mixed with ordinary questions renders through `RoleplayFlowStep`.
 */
const RoleplayPractice: React.FC<{
  flowName: string;
  flowRunId: number;
  courseId?: number | null;
  voiceModeAvailable?: boolean;
}> = ({ flowName, flowRunId, courseId = null, voiceModeAvailable = false }) => {
  const navigate = useNavigate();
  const { isEnabled: isDebugEnabled } = useDebug();
  const controller = useRoleplaySession(flowName, flowRunId);

  const continueCourse = async () => {
    try {
      const response = await courses.getFlowByName(flowName, { courseId });
      const nextFlowName = response.data?.next_flow?.name;
      const resolvedCourse = response.data?.course;
      if (typeof nextFlowName === 'string') {
        navigate(buildFlowPath({ flowName: nextFlowName, courseId: resolvedCourse?.id ?? courseId }));
        return;
      }
      if (typeof resolvedCourse?.id === 'number') {
        navigate(`/course_complete?courseId=${resolvedCourse.id}`, {
          state: { courseId: resolvedCourse.id, courseName: resolvedCourse.name, completedFlowName: flowName },
        });
        return;
      }
    } catch {
      // The training index is the safe fallback if next-module lookup is unavailable.
    }
    navigate(courseId ? `/training?courseId=${courseId}#courses` : '/training#courses');
  };

  return (
    <RoleplayScreens
      controller={controller}
      variant="page"
      onContinue={() => void continueCourse()}
      onExit={() => navigate(courseId ? `/training?courseId=${courseId}#courses` : '/training#courses')}
      onSafetyClose={() => navigate('/home')}
      showDebugPanel={isDebugEnabled}
      onStartGuidedVoice={voiceModeAvailable ? () => navigate(
        `${buildFlowPath({ flowName, flowRunId, courseId })}&mode=voice`,
      ) : undefined}
    />
  );
};

export default RoleplayPractice;
